// TerraProgressiveBuilder — Tier-A "Terra" renderer for the progressive-builder component.
//
// A React port of the "TERRA · A Living Lesson" design-exploration mockup
// (design-explorations/scenario-themes/terra-solarpunk/index.html), driven by the
// REAL ProgressiveBuilderSlideData + the same state machine as ProgressiveBuilderSlide's
// `Student`. The FOURTH consumer of the shared theme-world toolkit
// (lib/scenario-themes/) — validating it on an ORGANIC/light register (warm
// parchment, hand-drawn SVG growth, foley-synth earthy cues, pollen atmosphere).
//
// State machine + onActivityResponse contract mirror ProgressiveBuilderSlide exactly:
// `completedLayerIds` advances one layer at a time; the cumulative output morphs;
// on submit it emits
//   { activityType:'progressive-builder', mode:'survey',
//     responseData:{ layersCompleted, reflection } }.
//
// ★ THE STANDOUT MOMENT ★ — GROW AS ONE ORGANISM. Every "grow this layer" press
// simultaneously (a) blooms the next length of a self-drawing vine — a fresh
// leaf / bud / finally a flower unfurls — AND (b) morphs the output text into its
// next, clearer form, with an earthy sprout cue + a puff of pollen. The clearer
// the writing gets, the more alive the plant becomes: craft as gardening.

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import type { ProgressiveBuilderSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio, playTerraSprout } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';

const STYLE_ID = 'terra-pb-scoped-styles';

// Botanical palette constants (mockup-faithful, beyond the theme's two accents).
const SOIL = '#5a4326';
const SOIL_SOFT = '#7a5d39';
const LEAF = '#2f6e3b';
const LEAF_DEEP = '#21512b';
const SPROUT = '#7fb83f';
const TERRACOTTA = '#d2613c';
const HONEY = '#e6a82e';
const INK = '#3a2c18';

// Grow-parts revealed as the plant matures (bloom reserved for completion).
const NONBLOOM_PARTS = ['leafL1', 'leafR1', 'leafL2', 'budGroup'];

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — panels, seed-packets, plant stage, output sheet, reflection.
// Namespaced under `.terrapb`; injected once, ref-counted. (Dappled light, paper
// grain + pollen live in the toolkit's <ThemeWorldAtmosphere worldId="terra">.)
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.terrapb { position: relative; --ease-grow: cubic-bezier(.22,1,.36,1); }
.terrapb * { box-sizing: border-box; }

.terrapb .tpb-eyebrow {
  text-transform: uppercase; letter-spacing: .42em; font-size: .68rem;
  font-weight: 600; color: ${TERRACOTTA}; margin-bottom: 14px; padding-left: .42em;
}
.terrapb .tpb-seed-word { color: ${TERRACOTTA}; font-style: italic; }

/* progress seeds */
.terrapb .tpb-seed-track { display: flex; gap: 10px; justify-content: center; align-items: center; margin: 24px 0 4px; }
.terrapb .tpb-seed-dot {
  width: 13px; height: 13px; border-radius: 50% 50% 50% 0; transform: rotate(45deg);
  background: rgba(90,67,38,.22); border: 1.5px solid rgba(90,67,38,.3);
  transition: background .5s var(--ease-grow), box-shadow .5s, transform .5s;
}
.terrapb .tpb-seed-dot.filled { background: ${SPROUT}; border-color: ${LEAF}; box-shadow: 0 0 0 4px rgba(127,184,63,.22); }

/* garden grid */
.terrapb .tpb-garden { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1.05fr); gap: 30px; margin-top: 34px; align-items: stretch; }
@media (max-width: 900px) { .terrapb .tpb-garden { grid-template-columns: 1fr; } }

.terrapb .tpb-panel {
  position: relative; border-radius: 22px; padding: 26px;
  background: linear-gradient(180deg, rgba(255,250,236,.86), rgba(243,233,210,.9));
  box-shadow: 0 18px 40px -20px rgba(90,67,38,.45), inset 0 1px 0 rgba(255,255,255,.5);
}
.terrapb .tpb-panel::before {
  content: ""; position: absolute; inset: 5px; border-radius: 18px;
  border: 2px solid ${SOIL_SOFT}; opacity: .4; pointer-events: none;
}
.terrapb .tpb-panel-label {
  display: inline-flex; align-items: center; gap: 8px; font-size: .7rem; font-weight: 700;
  letter-spacing: .22em; text-transform: uppercase; color: ${SOIL_SOFT}; margin-bottom: 14px;
}
.terrapb .tpb-panel-label svg { width: 16px; height: 16px; }

/* plant panel */
.terrapb .tpb-plant-panel {
  background: radial-gradient(120% 80% at 50% 110%, rgba(127,184,63,.18), transparent 60%),
              linear-gradient(180deg, rgba(207,234,231,.55), rgba(243,233,210,.92));
  display: flex; flex-direction: column;
}
.terrapb .tpb-plant-stage { flex: 1; position: relative; min-height: 380px; display: flex; align-items: flex-end; justify-content: center; }
.terrapb .tpb-plant-stage svg { width: 100%; height: 100%; overflow: visible; }
.terrapb .tpb-pot {
  position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
  width: 200px; height: 70px; background: linear-gradient(180deg, ${TERRACOTTA}, #b14d2c);
  border-radius: 8px 8px 14px 14px; box-shadow: inset 0 6px 0 rgba(255,255,255,.14), 0 12px 22px -10px rgba(90,67,38,.6);
}
.terrapb .tpb-pot::before { content: ""; position: absolute; top: -13px; left: -8px; right: -8px; height: 22px; background: linear-gradient(180deg, #df6f48, ${TERRACOTTA}); border-radius: 8px; box-shadow: inset 0 4px 0 rgba(255,255,255,.16); }
.terrapb .tpb-pot::after { content: ""; position: absolute; top: 4px; left: 18px; right: 18px; height: 16px; background: ${SOIL}; border-radius: 50%; box-shadow: inset 0 3px 6px rgba(0,0,0,.4); }
.terrapb .tpb-caption { text-align: center; font-size: 1.1rem; color: ${LEAF_DEEP}; margin-top: 6px; min-height: 1.4em; transition: opacity .5s; }
.terrapb .tpb-caption .em { color: ${TERRACOTTA}; }

.terrapb .tpb-burst { position: absolute; inset: 0; pointer-events: none; overflow: visible; z-index: 5; }

/* output sheet */
.terrapb .tpb-output-panel { display: flex; flex-direction: column; }
.terrapb .tpb-sheet {
  flex: 1; background: #fffaf0; border-radius: 14px; padding: 22px 22px 26px;
  border: 1.5px solid rgba(90,67,38,.18); box-shadow: inset 0 1px 0 #fff, 0 6px 18px -12px rgba(90,67,38,.4);
  position: relative; overflow: hidden; min-height: 260px;
}
.terrapb .tpb-sheet::after {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background-image: repeating-linear-gradient(transparent, transparent 31px, rgba(90,67,38,.07) 32px); background-position: 0 56px;
}
.terrapb .tpb-output-text { position: relative; z-index: 1; white-space: pre-wrap; font-size: 1.02rem; line-height: 1.62; color: ${INK}; }
.terrapb .tpb-output-text.fresh { animation: tpbBloom .7s var(--ease-grow) both; }
@keyframes tpbBloom { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.terrapb .tpb-weak-note { font-size: .9rem; font-style: italic; color: rgba(90,67,38,.5); margin-top: 12px; }

/* layer controls — seed packets */
.terrapb .tpb-layers { grid-column: 1 / -1; margin-top: 28px; }
.terrapb .tpb-layers-title { text-align: center; font-size: 1.4rem; color: ${LEAF_DEEP}; margin-bottom: 4px; }
.terrapb .tpb-layers-sub { text-align: center; color: ${SOIL}; font-size: .95rem; margin-bottom: 20px; }
.terrapb .tpb-layer-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 16px; }

.terrapb .tpb-packet {
  position: relative; text-align: left; border: none; cursor: pointer;
  border-radius: 14px 14px 16px 16px; padding: 16px; color: #fff7e6;
  background: linear-gradient(180deg, ${SOIL_SOFT}, ${SOIL});
  box-shadow: 0 10px 22px -12px rgba(58,44,24,.8), inset 0 1px 0 rgba(255,255,255,.16);
  transition: transform .3s var(--ease-grow), box-shadow .3s, filter .3s, opacity .3s; overflow: hidden;
}
.terrapb .tpb-packet::before { content: ""; position: absolute; top: -1px; left: 0; right: 0; height: 9px; background: repeating-linear-gradient(135deg, ${HONEY} 0 7px, transparent 7px 14px); opacity: .9; }
.terrapb .tpb-packet .tpb-no {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; border-radius: 50% 50% 50% 0; transform: rotate(45deg);
  background: ${HONEY}; color: ${SOIL}; font-weight: 800; font-size: .9rem; margin-bottom: 12px;
}
.terrapb .tpb-packet .tpb-no span { transform: rotate(-45deg); }
.terrapb .tpb-packet .tpb-name { display: block; font-weight: 700; font-size: 1rem; line-height: 1.2; margin-bottom: 6px; color: #fff7e6; }
.terrapb .tpb-packet .tpb-add { display: block; font-size: .86rem; color: rgba(255,247,230,.72); line-height: 1.4; margin-bottom: 10px; }
.terrapb .tpb-packet .tpb-cta { display: inline-flex; align-items: center; gap: 7px; font-size: .82rem; font-weight: 700; letter-spacing: .03em; color: ${SPROUT}; }
.terrapb .tpb-packet .tpb-cta svg { width: 15px; height: 15px; }
.terrapb .tpb-packet:not(:disabled):hover { transform: translateY(-4px) rotate(-.6deg); box-shadow: 0 16px 30px -12px rgba(58,44,24,.85); filter: brightness(1.06); }
.terrapb .tpb-packet:not(:disabled):active { transform: translateY(-1px) scale(.985); }
.terrapb .tpb-packet.sown { background: linear-gradient(180deg, ${LEAF}, ${LEAF_DEEP}); opacity: .96; }
.terrapb .tpb-packet.sown .tpb-cta { color: #d6f0b6; }
.terrapb .tpb-packet.sown .tpb-no { background: ${SPROUT}; }
.terrapb .tpb-packet:disabled.locked { opacity: .5; cursor: not-allowed; filter: saturate(.5); }
.terrapb .tpb-packet .tpb-planted { position: absolute; top: 14px; right: 14px; font-size: 1.1rem; opacity: 0; transform: scale(.4); transition: opacity .4s var(--ease-grow), transform .4s var(--ease-grow); }
.terrapb .tpb-packet.sown .tpb-planted { opacity: 1; transform: scale(1); }

/* reflection (payoff register) */
.terrapb .tpb-reflection { grid-column: 1 / -1; margin-top: 30px; text-align: center; }
.terrapb .tpb-reflection-card {
  display: inline-block; max-width: 640px; padding: 30px 34px;
  background: linear-gradient(180deg, rgba(255,250,236,.95), rgba(230,217,182,.95));
  border-radius: 20px; border: 2px solid rgba(47,110,59,.3); box-shadow: 0 22px 44px -22px rgba(47,110,59,.55);
}
.terrapb .tpb-reflection-card .tpb-bloom-emoji { font-size: 2.4rem; display: block; margin-bottom: 8px; }
.terrapb .tpb-reflection-card h2 { font-weight: 400; font-size: 1.6rem; color: ${LEAF_DEEP}; margin: 0 0 10px; }
.terrapb .tpb-reflection-card p { color: ${SOIL}; font-size: 1.04rem; line-height: 1.55; margin: 0 0 16px; }
.terrapb .tpb-reflection textarea {
  width: 100%; max-width: 560px; padding: 12px 14px; border-radius: 12px; font-size: .98rem;
  border: 1.5px solid rgba(90,67,38,.28); background: #fffaf0; color: ${INK}; resize: vertical; margin-bottom: 14px;
  font-family: inherit;
}
.terrapb .tpb-reflection textarea:focus { outline: none; border-color: ${LEAF}; box-shadow: 0 0 0 3px rgba(47,110,59,.16); }
.terrapb .tpb-submit {
  font-weight: 700; font-size: .92rem; letter-spacing: .04em; color: #fff7e6; cursor: pointer;
  background: linear-gradient(180deg, ${TERRACOTTA}, #b14d2c); border: none; border-radius: 999px;
  padding: 13px 28px; box-shadow: 0 10px 24px -10px rgba(178,77,44,.8);
  transition: transform .3s var(--ease-grow), box-shadow .3s, opacity .3s;
}
.terrapb .tpb-submit:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 16px 30px -12px rgba(178,77,44,.85); }
.terrapb .tpb-submit:disabled { opacity: .55; cursor: default; }
.terrapb .tpb-thanks { color: ${LEAF_DEEP}; font-size: 1.05rem; }

@media (prefers-reduced-motion: reduce) {
  .terrapb .tpb-output-text.fresh { animation-duration: .25s; }
  .terrapb * { transition-duration: .25s !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // CSS is static; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function sproutIconMarkup() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21v-7" />
      <path d="M12 14c-3 0-5-2-5-5 3 0 5 2 5 5z" />
      <path d="M12 12c3-1 4-3 4-5-3 0-4 2-4 5z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function TerraProgressiveBuilder({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ProgressiveBuilderSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('terra');

  const headingFont = theme.fonts.heading;

  // ── State machine (mirrors ProgressiveBuilderSlide's Student) ─────────────────
  // The {layersCompleted, reflection} payload only fires on submit, so its
  // presence means the build finished — restore the completed/submitted view.
  const saved: Record<string, unknown> = savedResponse ?? {};
  const restored = Array.isArray(saved.layersCompleted);
  const [completedLayerIds, setCompletedLayerIds] = useState<string[]>(() =>
    restored ? [...(saved.layersCompleted as string[])] : [],
  );
  const [reflection, setReflection] = useState(
    restored && typeof saved.reflection === 'string' ? saved.reflection : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  // Fires onComplete exactly once at the terminal submit (already done if rehydrated).
  const completedRef = useRef(restored);

  const layerCount = data.layers.length;
  const currentLayerIdx = completedLayerIds.length;
  const allDone = currentLayerIdx >= layerCount && layerCount > 0;
  const hasReflectionPrompt = (data.reflection?.prompt ?? '').trim().length > 0;

  const currentOutput = allDone
    ? data.layers[layerCount - 1]?.transformedOutput ?? ''
    : currentLayerIdx === 0
      ? data.baseState
      : data.layers[currentLayerIdx - 1]?.transformedOutput ?? '';

  const addLayer = useCallback(() => {
    setCompletedLayerIds((prev) => {
      const next = data.layers[prev.length];
      return next ? [...prev, next.id] : prev;
    });
  }, [data.layers]);

  const submit = useCallback(() => {
    setSubmitted(true);
    play('advance');
    const responseData: Record<string, unknown> = { layersCompleted: completedLayerIds };
    // Only include reflection when the author actually set a prompt.
    if (hasReflectionPrompt) responseData.reflection = reflection;
    onActivityResponse?.({
      activityType: 'progressive-builder',
      mode: 'survey',
      responseData,
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [completedLayerIds, reflection, hasReflectionPrompt, onActivityResponse, onComplete, play]);

  // ── SVG plant refs + imperative growth ────────────────────────────────────────
  const stemRef = useRef<SVGPathElement | null>(null);
  const seedlingRef = useRef<SVGGElement | null>(null);
  const partRefs = useRef<Map<string, SVGGElement>>(new Map());
  const burstRef = useRef<HTMLDivElement | null>(null);
  const plantWrapRef = useRef<HTMLDivElement | null>(null);
  const stemLenRef = useRef(0);
  // Seed at the restored layer count so a rehydrated mount doesn't read as "grew"
  // and fire a spurious sprout cue / pollen puff.
  const prevIdxRef = useRef(restored ? completedLayerIds.length : 0);

  const petals = useMemo(() => {
    const N = 8;
    return Array.from({ length: N }).map((_, i) => {
      const a = (i / N) * Math.PI * 2;
      const cx = Math.cos(a) * 20;
      const cy = Math.sin(a) * 20;
      return {
        key: i,
        cx: cx.toFixed(1),
        cy: cy.toFixed(1),
        fill: i % 2 ? HONEY : TERRACOTTA,
        rot: (a * 180) / Math.PI + 90,
      };
    });
  }, []);

  // one-shot pollen puff (used on each grow + on completion)
  const pollenPuff = useCallback(
    (x: number, y: number) => {
      if (reduceMotion) return;
      const field = burstRef.current;
      if (!field) return;
      const rect = field.getBoundingClientRect();
      for (let i = 0; i < 9; i++) {
        const p = document.createElement('div');
        p.style.position = 'absolute';
        p.style.left = `${x - rect.left}px`;
        p.style.top = `${y - rect.top}px`;
        const sz = 4 + Math.random() * 5;
        p.style.width = `${sz}px`;
        p.style.height = `${sz}px`;
        p.style.borderRadius = '50%';
        p.style.background = 'radial-gradient(circle at 35% 35%, #fff6cf, #e6a82e)';
        p.style.boxShadow = '0 0 8px 2px rgba(230,168,46,.6)';
        p.style.pointerEvents = 'none';
        field.appendChild(p);
        const ang = Math.random() * Math.PI * 2;
        const dist = 28 + Math.random() * 64;
        const dx = Math.cos(ang) * dist;
        const dy = Math.sin(ang) * dist - 36;
        p.animate(
          [
            { transform: 'translate(0,0) scale(1)', opacity: 1 },
            { transform: `translate(${dx}px,${dy}px) scale(.2)`, opacity: 0 },
          ],
          { duration: 850 + Math.random() * 500, easing: 'cubic-bezier(.22,1,.36,1)' },
        ).onfinish = () => p.remove();
      }
    },
    [reduceMotion],
  );

  // prep the stem self-draw + hide grow parts, once on mount
  useEffect(() => {
    const stem = stemRef.current;
    if (stem) {
      const len = stem.getTotalLength();
      stemLenRef.current = len;
      stem.style.strokeDasharray = String(len);
      stem.style.strokeDashoffset = String(len);
    }
    partRefs.current.forEach((el) => {
      el.style.transformOrigin = '150px 410px';
      el.style.transition = 'none';
      el.style.opacity = '0';
      el.style.transform = 'scale(0.01)';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // grow the plant in step with the output as layers are added
  useEffect(() => {
    const prev = prevIdxRef.current;
    const idx = currentLayerIdx;
    prevIdxRef.current = idx;
    const grew = idx > prev;

    // 1. grow the stem proportionally
    const stem = stemRef.current;
    const len = stemLenRef.current;
    if (stem && len > 0) {
      const frac = layerCount ? idx / layerCount : 0;
      const target = len * (1 - frac);
      if (reduceMotion) {
        stem.style.strokeDashoffset = String(target);
      } else {
        const from = parseFloat(stem.style.strokeDashoffset || String(len));
        const t0 = performance.now();
        const dur = 900;
        const tick = (now: number) => {
          const k = Math.min(1, (now - t0) / dur);
          const e = 1 - Math.pow(1 - k, 3);
          stem.style.strokeDashoffset = String(from + (target - from) * e);
          if (k < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }

    // 2. reveal leaves/bud up to progress (bloom reserved for completion)
    const frac = layerCount ? idx / layerCount : 0;
    const revealedLeaves = allDone ? NONBLOOM_PARTS.length : Math.ceil(frac * NONBLOOM_PARTS.length);
    const revealPart = (id: string, delay: number) => {
      const el = partRefs.current.get(id);
      if (!el) return;
      window.setTimeout(() => {
        el.style.transition = reduceMotion
          ? 'opacity .35s ease'
          : 'transform 1s cubic-bezier(.22,1.4,.36,1), opacity .6s ease';
        el.style.opacity = '1';
        el.style.transform = 'scale(1)';
      }, reduceMotion ? 0 : delay);
    };
    NONBLOOM_PARTS.forEach((id, i) => {
      if (i < revealedLeaves) revealPart(id, 200 + i * 110);
    });
    if (allDone) revealPart('bloomGroup', 380);

    // 3. fade the seedling once growth begins
    if (idx >= 1 && seedlingRef.current) {
      seedlingRef.current.style.transition = 'opacity .8s ease';
      seedlingRef.current.style.opacity = '0';
    }

    // 4. cue + pollen only when a layer was actually grown (not on mount)
    if (grew) {
      playWith((e) => playTerraSprout(e, idx - 1));
      const wrap = plantWrapRef.current;
      if (wrap) {
        const r = wrap.getBoundingClientRect();
        pollenPuff(r.left + r.width / 2, r.top + r.height * 0.4);
      }
      if (allDone) {
        play('reveal');
        const wrap2 = plantWrapRef.current;
        if (wrap2 && !reduceMotion) {
          const r = wrap2.getBoundingClientRect();
          for (let i = 0; i < 4; i++) {
            window.setTimeout(() => pollenPuff(r.left + r.width / 2, r.top + r.height * 0.25), i * 170);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLayerIdx, allDone]);

  const nextLayer = !allDone ? data.layers[currentLayerIdx] : undefined;

  const caption = allDone
    ? 'Fully bloomed. — every layer did one job.'
    : currentLayerIdx === 0
      ? 'A thirsty little seedling. — give it a clear first layer.'
      : `It grows. — ${nextLayer?.label ?? ''}`;
  const [capHead, capEm] = caption.split(' — ');

  return (
    <div
      className="terrapb h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: INK, fontFamily: theme.fonts.body }}
    >
      <ThemeWorldAtmosphere
        worldId="terra"
        primary={LEAF}
        accent={TERRACOTTA}
        background={theme.colors.background}
        style={{ zIndex: 0 }}
      />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 2 }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '84px 28px 72px' }}>
          {/* intro */}
          <header style={{ textAlign: 'center', marginBottom: 6 }}>
            <div className="tpb-eyebrow">Terra · A Living Lesson</div>
            <h1
              style={{
                fontFamily: headingFont,
                fontWeight: 400,
                fontSize: 'clamp(2.3rem, 6vw, 4.2rem)',
                lineHeight: 0.98,
                letterSpacing: '-0.01em',
                color: LEAF_DEEP,
                margin: 0,
                textShadow: '0 1px 0 rgba(255,255,255,.4)',
              }}
            >
              {data.title}
            </h1>
            {data.subtitle && (
              <p style={{ maxWidth: 560, margin: '14px auto 0', fontSize: '1.05rem', lineHeight: 1.6, color: SOIL }}>
                {data.subtitle}
              </p>
            )}
            <div className="tpb-seed-track" aria-hidden>
              {data.layers.map((l, i) => (
                <div key={l.id} className={`tpb-seed-dot${i < currentLayerIdx ? ' filled' : ''}`} />
              ))}
            </div>
          </header>

          <section className="tpb-garden">
            {/* PLANT */}
            <div className="tpb-panel tpb-plant-panel">
              <div className="tpb-panel-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
                  <path d="M12 21c0-5 0-8 0-11" />
                  <path d="M12 11c-3-1-5-3-5-6 3 0 5 2 5 6z" />
                  <path d="M12 14c3-1 5-2 5-5-3 0-5 2-5 5z" />
                </svg>
                The Living Plant
              </div>
              <div className="tpb-plant-stage" ref={plantWrapRef}>
                <svg viewBox="0 0 300 440" preserveAspectRatio="xMidYMax meet" aria-hidden>
                  <defs>
                    <linearGradient id="tpbStemGrad" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0" stopColor="#3c7a2d" />
                      <stop offset="1" stopColor="#7fb83f" />
                    </linearGradient>
                    <radialGradient id="tpbBloomGrad" cx="40%" cy="35%" r="70%">
                      <stop offset="0" stopColor="#ffe9a8" />
                      <stop offset="55%" stopColor="#e6a82e" />
                      <stop offset="100%" stopColor="#d2613c" />
                    </radialGradient>
                    <radialGradient id="tpbLeafGrad" cx="30%" cy="30%" r="80%">
                      <stop offset="0" stopColor="#9ccf57" />
                      <stop offset="100%" stopColor="#2f6e3b" />
                    </radialGradient>
                    <radialGradient id="tpbBudGrad" cx="40%" cy="30%" r="75%">
                      <stop offset="0" stopColor="#9bd06a" />
                      <stop offset="100%" stopColor="#3c7a2d" />
                    </radialGradient>
                  </defs>

                  <path
                    ref={stemRef}
                    d="M150 410 C150 360 142 320 146 280 C150 240 138 205 150 168 C160 138 150 110 150 78"
                    fill="none"
                    stroke="url(#tpbStemGrad)"
                    strokeWidth={9}
                    strokeLinecap="round"
                  />

                  <g ref={seedlingRef}>
                    <path d="M150 410 C150 396 150 388 150 380" fill="none" stroke="#7a5d39" strokeWidth={5} strokeLinecap="round" />
                    <path d="M150 384 C140 384 132 388 128 396 C137 396 145 392 150 386 Z" fill="#9aa86a" />
                    <path d="M150 384 C160 384 168 388 172 396 C163 396 155 392 150 386 Z" fill="#9aa86a" />
                  </g>

                  <g ref={(el) => { if (el) partRefs.current.set('leafL1', el); }}>
                    <path d="M148 300 C112 300 86 282 78 252 C116 250 142 268 150 298 Z" fill="url(#tpbLeafGrad)" />
                    <path d="M148 298 C124 290 104 278 86 260" fill="none" stroke="#21512b" strokeWidth={2} opacity={0.5} />
                  </g>
                  <g ref={(el) => { if (el) partRefs.current.set('leafR1', el); }}>
                    <path d="M148 235 C184 235 210 217 218 187 C180 185 154 203 146 233 Z" fill="url(#tpbLeafGrad)" />
                    <path d="M148 233 C172 225 192 213 210 195" fill="none" stroke="#21512b" strokeWidth={2} opacity={0.5} />
                  </g>
                  <g ref={(el) => { if (el) partRefs.current.set('leafL2', el); }}>
                    <path d="M150 178 C118 174 96 156 92 128 C124 130 146 148 152 176 Z" fill="url(#tpbLeafGrad)" />
                  </g>
                  <g ref={(el) => { if (el) partRefs.current.set('budGroup', el); }}>
                    <path d="M150 120 C150 100 150 92 150 80" fill="none" stroke="url(#tpbStemGrad)" strokeWidth={7} strokeLinecap="round" />
                    <ellipse cx={150} cy={74} rx={15} ry={22} fill="url(#tpbBudGrad)" />
                    <path d="M138 80 C146 70 154 70 162 80" fill="none" stroke="#21512b" strokeWidth={2} opacity={0.5} />
                  </g>
                  {/* Outer group is attribute-transform-free so the reveal's
                      style.transform=scale() won't clobber positioning; the inner
                      group holds the fixed translate to the stem tip. */}
                  <g ref={(el) => { if (el) partRefs.current.set('bloomGroup', el); }}>
                    <g transform="translate(150 66)">
                      {petals.map((p) => (
                        <ellipse
                          key={p.key}
                          cx={p.cx}
                          cy={p.cy}
                          rx={11}
                          ry={20}
                          fill={p.fill}
                          opacity={0.95}
                          transform={`rotate(${p.rot} ${p.cx} ${p.cy})`}
                        />
                      ))}
                      <circle cx={0} cy={0} r={15} fill="url(#tpbBloomGrad)" />
                      <circle cx={0} cy={0} r={8} fill="#5a4326" opacity={0.85} />
                      <circle cx={-3} cy={-3} r={2.4} fill="#ffe9a8" />
                    </g>
                  </g>
                </svg>
                <div className="tpb-pot" />
                <div className="tpb-burst" ref={burstRef} />
              </div>
              <div className="tpb-caption" style={{ fontFamily: headingFont }}>
                {capHead}
                {capEm && <> <span className="em">{capEm}</span></>}
              </div>
            </div>

            {/* OUTPUT */}
            <div className="tpb-panel tpb-output-panel">
              <div className="tpb-panel-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
                The output · so far
              </div>
              <div className="tpb-sheet">
                <div key={currentOutput} className="tpb-output-text fresh">
                  {currentOutput || '(empty)'}
                </div>
                {currentLayerIdx === 0 && (
                  <p className="tpb-weak-note">…the starting point. Tend it, one layer at a time.</p>
                )}
              </div>
            </div>

            {/* LAYER CONTROLS */}
            <div className="tpb-layers">
              <div className="tpb-layers-title" style={{ fontFamily: headingFont }}>
                Tend it, one layer at a time
              </div>
              <div className="tpb-layers-sub">Each addition does one job — and grows the plant one stage.</div>
              <div className="tpb-layer-grid">
                {data.layers.map((layer, i) => {
                  const sown = i < currentLayerIdx;
                  const isCurrent = i === currentLayerIdx && !allDone;
                  return (
                    <button
                      key={layer.id}
                      type="button"
                      className={`tpb-packet${sown ? ' sown' : ''}${!sown && !isCurrent ? ' locked' : ''}`}
                      disabled={!isCurrent}
                      onClick={isCurrent ? addLayer : undefined}
                      onMouseEnter={isCurrent ? () => play('hover') : undefined}
                    >
                      <span className="tpb-planted">🌱</span>
                      <span className="tpb-no"><span>{i + 1}</span></span>
                      <span className="tpb-name">{layer.label}</span>
                      <span className="tpb-add">{layer.addition}</span>
                      <span className="tpb-cta">{sproutIconMarkup()} {sown ? 'grown' : 'grow this layer'}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* FINISH / REFLECTION — the finish affordance ALWAYS renders once the
                build is fully grown; it is NOT gated on a reflection prompt
                existing. When the author set a prompt we show it WITH a textarea
                (CTA "Plant your reflection"); otherwise the same card just carries
                a "Harvest your work" button so the build can always be completed
                and onComplete/onActivityResponse always fire. */}
            {allDone && (
              <div className="tpb-reflection">
                <div className="tpb-reflection-card">
                  <span className="tpb-bloom-emoji">🌼</span>
                  <h2 style={{ fontFamily: headingFont }}>Fully bloomed.</h2>
                  {hasReflectionPrompt && <p>{data.reflection.prompt}</p>}
                  {!submitted ? (
                    <>
                      {hasReflectionPrompt && (
                        <textarea
                          value={reflection}
                          onChange={(e) => setReflection(e.target.value)}
                          rows={3}
                          placeholder="Your thoughts…"
                          aria-label="Reflection"
                        />
                      )}
                      <div>
                        <button type="button" className="tpb-submit" onClick={submit}>
                          {hasReflectionPrompt ? 'Plant your reflection' : 'Harvest your work'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="tpb-thanks">🌿 Planted. Thank you.</p>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
